import { useEffect, useRef, useState } from "react";
import { PREFERRED_SOURCE_DEEPLINK, SITE } from "@/lib/site";
import { recordCtaClick } from "@/lib/analytics.functions";

declare global {
  interface Window {
    PREFERRED_SOURCE?: Array<(api: PreferredSourceApi) => void>;
  }
}

interface PreferredSourceApi {
  init: (opts: { theme?: "light" | "dark"; lang?: string }) => void;
  addPreferredSource: () => void;
}

const SCRIPT_SRC = "https://news.google.com/swg/js/v1/publisher.js";

/**
 * Loads Google's Preferred Sources library once per page, in manual mode so
 * the declarative auto-scan never races client-side route changes. Resolves
 * with the API object, or null when the script is blocked.
 */
let apiPromise: Promise<PreferredSourceApi | null> | null = null;

function loadPreferredSource(): Promise<PreferredSourceApi | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<PreferredSourceApi | null>((resolve) => {
    window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
    window.PREFERRED_SOURCE.push((api) => {
      try {
        api.init({ theme: "light" });
      } catch {
        // init is best-effort; the flow still works with defaults.
      }
      resolve(api);
    });

    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement("script");
      s.async = true;
      s.src = SCRIPT_SRC;
      s.setAttribute("preferred-sources-control", "manual");
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    }

    // If Google never answers (blocked, offline, ad blocker), fall back.
    window.setTimeout(() => resolve(null), 6000);
  });

  return apiPromise;
}

interface Props {
  /** Where on the site this unit sits — used for click measurement. */
  placement: "article" | "home" | "about" | "footer" | "header";
  /** Compact single-line treatment for the footer. */
  variant?: "card" | "inline";
  className?: string;
}

export function PreferredSourceButton({ placement, variant = "card", className }: Props) {
  const [ready, setReady] = useState(false);
  const apiRef = useRef<PreferredSourceApi | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadPreferredSource().then((api) => {
      if (cancelled || !api) return;
      apiRef.current = api;
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function track() {
    void recordCtaClick({ data: { name: "preferred_source", placement } }).catch(() => {});
  }

  function onClick(e: React.MouseEvent<HTMLAnchorElement>) {
    track();
    // With the library loaded we run Google's in-page flow, which returns the
    // reader to this article. Without it, the anchor's deeplink takes over.
    if (apiRef.current) {
      e.preventDefault();
      try {
        apiRef.current.addPreferredSource();
      } catch {
        window.open(PREFERRED_SOURCE_DEEPLINK, "_blank", "noopener");
      }
    }
  }

  const label = "Add Map Observer to your preferred sources in Google";

  const anchor = (
    <a
      href={PREFERRED_SOURCE_DEEPLINK}
      target={ready ? undefined : "_blank"}
      rel="noopener noreferrer"
      onClick={onClick}
      aria-label={label}
      className={
        variant === "inline"
          ? "inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors underline underline-offset-4"
          : "inline-flex items-center gap-2 border border-foreground px-4 py-3 min-h-11 text-sm font-medium text-foreground hover:bg-foreground hover:text-background transition-colors"
      }
    >
      <GoogleGlyph />
      {variant === "inline" ? (
        <>
          <span className="hidden sm:inline">Add as a preferred source</span>
          <span className="sm:hidden">Add to Google</span>
        </>
      ) : (
        "Add as a preferred source"
      )}
    </a>
  );

  if (variant === "inline") {
    return <div className={className}>{anchor}</div>;
  }

  return (
    <section
      className={`border border-border bg-paper-soft p-6 sm:p-7 ${className ?? ""}`}
      aria-labelledby={`preferred-source-${placement}`}
    >
      <p className="eyebrow">Follow us in Google</p>
      <h2 id={`preferred-source-${placement}`} className="headline-display mt-2 text-xl sm:text-2xl">
        See {SITE.name} first in Top Stories
      </h2>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Google lets you choose the publications you want to see more of. Pick {SITE.name} and our
        local search reporting is surfaced higher in Top Stories — and badged as preferred in AI
        Overviews and AI Mode — whenever you search.
      </p>
      <div className="mt-4">{anchor}</div>
    </section>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.81z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.94-2.92l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.94H1.28v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.29 14.29a7.2 7.2 0 0 1 0-4.58v-3.1H1.28a12 12 0 0 0 0 10.78z" />
      <path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.44-3.44C17.95 1.19 15.23 0 12 0A12 12 0 0 0 1.28 6.61l4.01 3.1C6.23 6.86 8.88 4.75 12 4.75z" />
    </svg>
  );
}
